// Audit readiness (APP-004 R3 P5, UI-P7).
//
// Types are transcribed from the frozen Policy AI contract. Two boundaries
// carry this family:
//
//  * Readiness is deterministic. The score, the six components, the effective
//    weights left after an omitted component's weight was redistributed, and
//    the inputs hash are all produced by the backend calculator from counted
//    governed records. Nothing here recomputes any of it, and no AI output
//    reaches it: an unverified AI mapping simply does not count as covered.
//  * Findings and project status are backend state machines. `resolved` and
//    `accepted` are terminal (a recurrence is a new finding, so there is no
//    reopen route and no reopen method), closure is refused while a finding is
//    live or no snapshot has been frozen, and a finding owner may not close
//    their own finding during fieldwork. Each refusal is a 409 in the
//    backend's own words, shown against the record that produced it.
import { GovernanceHttp, queryString } from "./governanceHttp";
import type { GovernanceClientOptions } from "./governanceHttp";

export type AuditProjectStatus =
  | "planning"
  | "fieldwork"
  | "remediation"
  | "closed"
  | "cancelled";

export type AuditScopeType = "framework_version" | "requirement" | "business_unit" | "system";

export type FindingSeverity = "low" | "medium" | "high" | "critical";

export type FindingStatus = "open" | "in_remediation" | "resolved" | "accepted" | "disputed";

export type ReadinessComponentKey =
  | "policy_coverage"
  | "evidence_coverage"
  | "evidence_freshness"
  | "findings_clear"
  | "exception_containment"
  | "remediation_timeliness";

export interface AuditProject {
  id: string;
  organizationId: string;
  name: string;
  description: string;
  frameworkVersionId: string | null;
  auditorName: string;
  leadId: string;
  status: AuditProjectStatus;
  periodStart: string | null;
  periodEnd: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  closedAt: string | null;
  closedBy: string | null;
}

/**
 * One scope reference. Framework versions and requirements are real records;
 * business units and systems are free references because Policy AI has no
 * registry for them, so `scopeRef` is whatever the tenant typed and Studio has
 * validated nothing about it.
 */
export interface AuditScopeEntry {
  id: string;
  organizationId: string;
  auditProjectId: string;
  scopeType: AuditScopeType;
  scopeRef: string;
  label: string;
  included: boolean;
  exclusionRationale: string;
  excludedBy: string | null;
  excludedAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface AuditFinding {
  id: string;
  organizationId: string;
  auditProjectId: string;
  findingKey: string;
  title: string;
  description: string;
  severity: FindingSeverity;
  status: FindingStatus;
  ownerId: string;
  requirementId: string | null;
  policyId: string | null;
  dueAt: string | null;
  raisedBy: string;
  raisedAt: string;
  decidedBy: string | null;
  decidedAt: string | null;
  workflowInstanceId: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Append only: a transition is recorded, never edited. */
export interface FindingTransition {
  id: string;
  organizationId: string;
  findingId: string;
  actorId: string;
  fromStatus: FindingStatus;
  toStatus: FindingStatus;
  rationale: string;
  decidedAt: string;
}

/**
 * One explainable score component. `weight` is the published weight and
 * `effectiveWeight` is what it actually contributed after the backend
 * redistributed the weight of every omitted (zero denominator) component. A
 * component with `included: false` did not apply; it did not fail.
 */
export interface ReadinessComponent {
  key: ReadinessComponentKey;
  label: string;
  numerator: number;
  denominator: number;
  ratio: number | null;
  weight: number;
  effectiveWeight: number;
  included: boolean;
  detail: string;
}

export interface ReadinessScore {
  auditProjectId: string;
  frameworkVersionId: string | null;
  score: number;
  method: "deterministic";
  calculatorVersion: string;
  components: ReadinessComponent[];
  excludedRequirementIds: string[];
  /** Identical governed inputs always hash identically. */
  inputsHash: string;
  generatedAt: string;
}

/** A frozen position. Snapshots are insert only and never change. */
export interface ReadinessSnapshot {
  id: string;
  organizationId: string;
  auditProjectId: string;
  frameworkVersionId: string | null;
  score: number;
  method: "deterministic";
  calculatorVersion: string;
  components: ReadinessComponent[];
  excludedRequirementIds: string[];
  inputsHash: string;
  generatedBy: string;
  generatedAt: string;
}

export interface CoverageRow {
  requirementId: string;
  requirementKey: string;
  title: string;
  inScope: boolean;
  policyCovered: boolean;
  evidenceCovered: boolean;
  evidenceFresh: boolean;
  openFindings: number;
}

export interface CoverageRollup {
  auditProjectId: string;
  frameworkVersionId: string | null;
  inScopeCount: number;
  excludedCount: number;
  rows: CoverageRow[];
}

/** An evidence package as captured when it was linked: the hash is a record. */
export interface AuditPackageLink {
  id: string;
  organizationId: string;
  auditProjectId: string;
  evidencePackageId: string;
  manifestHash: string;
  itemCount: number;
  linkedBy: string;
  linkedAt: string;
}

export interface FindingSweepResult {
  due: number;
  overdue: number;
}

export interface CreateAuditProjectInput {
  name: string;
  description?: string;
  frameworkVersionId?: string | null;
  auditorName?: string;
  leadId: string;
  periodStart?: string | null;
  periodEnd?: string | null;
}

export interface AddAuditScopeInput {
  scopeType: AuditScopeType;
  scopeRef: string;
  label?: string;
}

export interface RaiseFindingInput {
  findingKey?: string;
  title: string;
  description?: string;
  severity?: FindingSeverity;
  ownerId: string;
  requirementId?: string | null;
  policyId?: string | null;
  dueAt?: string | null;
}

export interface FindingFilter {
  status?: FindingStatus;
  severity?: FindingSeverity;
  ownerId?: string;
}

/**
 * Audit readiness through the Studio Worker proxy. Every route below is
 * `/organizations/{orgId}/policy-ai/...`: the browser never reaches Policy AI
 * directly, so the entitlement pair and the per-user permission are always
 * checked at the edge before a call leaves Studio.
 */
export class AuditClient {
  private http: GovernanceHttp;

  constructor(opts: GovernanceClientOptions) {
    this.http = new GovernanceHttp(opts);
  }

  // ----- audit projects -----------------------------------------------------
  projects(orgId: string, status?: AuditProjectStatus) {
    return this.http.get<{ auditProjects: AuditProject[] }>(
      orgId,
      `audit-projects${queryString({ status })}`,
    );
  }
  project(orgId: string, auditProjectId: string) {
    return this.http.get<{ auditProject: AuditProject }>(orgId, `audit-projects/${auditProjectId}`);
  }
  createProject(orgId: string, input: CreateAuditProjectInput) {
    return this.http.post<{ auditProject: AuditProject }>(orgId, "audit-projects", input);
  }
  /**
   * Moves the project. Closure is refused while any finding is still live or
   * no readiness snapshot has been frozen; both are 409s in the backend's
   * words, not something to predict here.
   */
  setProjectStatus(orgId: string, auditProjectId: string, status: AuditProjectStatus) {
    return this.http.post<{ auditProject: AuditProject }>(
      orgId,
      `audit-projects/${auditProjectId}/status`,
      { status },
    );
  }

  // ----- scope and exclusions -----------------------------------------------
  scope(orgId: string, auditProjectId: string) {
    return this.http.get<{ scope: AuditScopeEntry[] }>(
      orgId,
      `audit-projects/${auditProjectId}/scope`,
    );
  }
  addScope(orgId: string, auditProjectId: string, input: AddAuditScopeInput) {
    return this.http.post<{ scopeEntry: AuditScopeEntry }>(
      orgId,
      `audit-projects/${auditProjectId}/scope`,
      input,
    );
  }
  /** An exclusion needs a rationale; the actor and timestamp are recorded. */
  excludeScope(orgId: string, auditProjectId: string, scopeId: string, rationale: string) {
    return this.http.post<{ scopeEntry: AuditScopeEntry }>(
      orgId,
      `audit-projects/${auditProjectId}/scope/${scopeId}/exclusion`,
      { rationale },
    );
  }
  /** Reinstates an excluded entry by removing the exclusion. */
  includeScope(orgId: string, auditProjectId: string, scopeId: string) {
    return this.http.del<{ scopeEntry: AuditScopeEntry }>(
      orgId,
      `audit-projects/${auditProjectId}/scope/${scopeId}/exclusion`,
    );
  }

  // ----- findings -----------------------------------------------------------
  findings(orgId: string, auditProjectId: string, filter: FindingFilter = {}) {
    return this.http.get<{ findings: AuditFinding[] }>(
      orgId,
      `audit-projects/${auditProjectId}/findings${queryString({ ...filter })}`,
    );
  }
  raiseFinding(orgId: string, auditProjectId: string, input: RaiseFindingInput) {
    return this.http.post<{ finding: AuditFinding }>(
      orgId,
      `audit-projects/${auditProjectId}/findings`,
      input,
    );
  }
  finding(orgId: string, findingId: string) {
    return this.http.get<{ finding: AuditFinding }>(orgId, `audit-findings/${findingId}`);
  }
  findingHistory(orgId: string, findingId: string) {
    return this.http.get<{ history: FindingTransition[] }>(
      orgId,
      `audit-findings/${findingId}/history`,
    );
  }
  /**
   * Moves a finding. `resolved` and `accepted` are terminal, and during
   * fieldwork the finding's own owner is refused when closing it: someone else
   * has to. Neither rule is mirrored here.
   */
  setFindingStatus(orgId: string, findingId: string, status: FindingStatus, rationale?: string) {
    return this.http.post<{ finding: AuditFinding; transition: FindingTransition }>(
      orgId,
      `audit-findings/${findingId}/status`,
      { status, ...(rationale === undefined ? {} : { rationale }) },
    );
  }
  /** Idempotent: a finding is chased once, so a repeat run reports zero. */
  runFindingSweep(orgId: string) {
    return this.http.post<FindingSweepResult>(orgId, "audit-findings/overdue/run", {});
  }

  // ----- evidence packages --------------------------------------------------
  packages(orgId: string, auditProjectId: string) {
    return this.http.get<{ packages: AuditPackageLink[] }>(
      orgId,
      `audit-projects/${auditProjectId}/packages`,
    );
  }
  /**
   * Links a package that Evidence already froze. The manifest hash and item
   * count are captured by the backend at link time, so the audit records the
   * package as it stood.
   */
  linkPackage(orgId: string, auditProjectId: string, evidencePackageId: string) {
    return this.http.post<{ package: AuditPackageLink }>(
      orgId,
      `audit-projects/${auditProjectId}/packages`,
      { evidencePackageId },
    );
  }

  // ----- readiness ----------------------------------------------------------
  /** The live deterministic position. Display it; never recompute it. */
  readiness(orgId: string, auditProjectId: string) {
    return this.http.get<{ readiness: ReadinessScore }>(
      orgId,
      `audit-projects/${auditProjectId}/readiness`,
    );
  }
  coverage(orgId: string, auditProjectId: string) {
    return this.http.get<{ coverage: CoverageRollup }>(
      orgId,
      `audit-projects/${auditProjectId}/coverage`,
    );
  }
  snapshots(orgId: string, auditProjectId: string) {
    return this.http.get<{ snapshots: ReadinessSnapshot[] }>(
      orgId,
      `audit-projects/${auditProjectId}/readiness/snapshots`,
    );
  }
  /**
   * Freezes the current position. Snapshots are keyed by their inputs hash, so
   * refreezing an unchanged position returns the original row with
   * `created: false` rather than writing a second one.
   */
  freezeReadiness(orgId: string, auditProjectId: string) {
    return this.http.post<{ snapshot: ReadinessSnapshot; created: boolean }>(
      orgId,
      `audit-projects/${auditProjectId}/readiness/snapshots`,
      {},
    );
  }
  snapshot(orgId: string, snapshotId: string) {
    return this.http.get<{ snapshot: ReadinessSnapshot }>(
      orgId,
      `readiness-snapshots/${snapshotId}`,
    );
  }
}
